<template>
  <div class="space-y-6">
    <div
      v-if="resetPasswordStep === ResetPasswordStep.Email"
      class="space-y-6"
    >
      <vetster-input
        v-model.trim="v$.inputs.email.$model"
        name="email"
        :label="$t('common.email')"
        autocomplete="off"
        :shake="shouldShake('email', v$.inputs.email.$error)"
        :error="hasError('email', v$.inputs.email.$error)"
        :feedback="getErrorMessage('email', v$.inputs.email.$error, $t('login.errors.please-enter-email'))"
        is-rebranded
      />
      <vetster-button
        type="button"
        variant="ocean"
        size="lg"
        :disabled="loading"
        :is-loading="loading"
        class="w-full"
        @click="requestResetCode(false)"
      >
        {{ $t('app.continue') }}
      </vetster-button>
    </div>
    <div
      v-else-if="resetPasswordStep === ResetPasswordStep.Code"
      class="grid justify-items-center space-y-6"
    >
      <vetster-verification-code-input
        ref="codeInput"
        :code-length="codeLength"
        :error="error"
        :disabled="loading"
        @complete="complete"
      />
      <a
        href="#"
        class="text-ocean-950 text-sm"
        :class="canResendCode ? 'underline' : 'cursor-not-allowed'"
        @click.prevent="canResendCode ? requestResetCode(true) : null"
      >
        {{ resendText }}
      </a>
      <vetster-loading :is-loading="loading" />
    </div>
    <div
      v-else-if="resetPasswordStep === ResetPasswordStep.Password"
      class="grid justify-items-center space-y-6"
    >
      <div class="w-full">
        <vetster-input
          ref="passwordInput"
          v-model.trim="v$.inputsConfirm.password.$model"
          :label="$t('common.password')"
          name="password"
          type="password"
          autocomplete="new-password"
          :lazy-update="false"
          is-rebranded
          :disabled="loading"
          :shake="shouldShake('password', v$.inputsConfirm.password.$error)"
          :error="hasError('password', v$.inputsConfirm.password.$error)"
          :feedback="getErrorMessage('password', v$.inputsConfirm.password.$error)"
        />
        <vetster-password-strength
          v-if="v$.inputsConfirm.password.$dirty"
          :password="inputsConfirm.password"
        />
      </div>
      <vetster-button
        type="button"
        variant="ocean"
        class="w-full"
        size="lg"
        :disabled="loading"
        :is-loading="loading"
        @click.prevent="resetPassword"
      >
        {{ $t('app.continue') }}
      </vetster-button>
    </div>
  </div>
</template>

<script>
import useVuelidate from '@vuelidate/core';
import { email, required } from '@vuelidate/validators';
import { mapGetters } from 'vuex';

import VetsterLoading from '@/brand-components/VetsterLoading';
import VetsterPasswordStrength from '@/brand-components/VetsterPasswordStrength';
import VetsterVerificationCodeInput from '@/brand-components/VetsterVerificationCodeInput';
import { passwordStrength } from '@/config';
import CodeLength from '@/enums/CodeLength';
import ResetPasswordStep from '@/enums/ResetPasswordStep';
import ParseErrors from '@/mixins/ParseErrors';

export default {
  name: 'VetsterResetPassword',

  components: {
    VetsterLoading,
    VetsterPasswordStrength,
    VetsterVerificationCodeInput,
  },

  mixins: [ParseErrors],

  props: {
    email: {
      type: String,
      required: true,
    },
  },

  setup() {
    return { v$: useVuelidate() };
  },

  validations: {
    inputs: {
      email: {
        required,
        email,
      },
    },
    inputsConfirm: {
      code: {
        required,
      },
      password: {
        required,
        passwordStrength,
      },
    },
  },

  data() {
    return {
      error: null,
      inputs: {
        email: '',
      },
      inputsConfirm: {
        code: '',
        password: '',
      },
      loading: false,
      ResetPasswordStep,
      timer: 0,
    };
  },

  computed: {
    ...mapGetters('authModal', ['resetPasswordStep']),
    codeLength() {
      return CodeLength.ResetPassword;
    },
    getPayload() {
      return {
        email: this.inputs.email.toLowerCase(),
      };
    },
    getConfirmPayload() {
      return {
        email: this.inputs.email.toLowerCase(),
        code: this.inputsConfirm.code,
        password: this.inputsConfirm.password,
      };
    },
    getAuthPayload() {
      return {
        email: this.inputs.email.toLowerCase(),
        password: this.inputsConfirm.password,
      };
    },
    canResendCode() {
      return this.timer <= 0;
    },
    resendText() {
      return this.canResendCode
        ? this.$t('login.resend-code')
        : `${this.$t('login.didnt-get-it')} ${this.$t('login.resend-in-seconds', { seconds: this.secondsRemaining })}`;
    },
    secondsRemaining() {
      return Math.round(this.timer / 1000);
    },
  },

  watch: {
    resetPasswordStep(step) {
      this.$store.commit('authModal/setHeader', this.$t('login.reset-your-password'));
      switch (step) {
        case ResetPasswordStep.Email:
          this.$store.commit('authModal/setHeaderDescription', this.$t('login.enter-your-email'));
          break;
        case ResetPasswordStep.Code:
          this.$store.commit('authModal/setHeaderDescription', this.$t('login.we-sent-a-verification-code-to', { email: this.inputs.email }));
          setTimeout(() => {
            this.$refs.codeInput.focusNextEmptyField();
          }, 200);
          break;
        case ResetPasswordStep.Password:
          this.$store.commit('authModal/setHeaderDescription', this.$t('login.your-password-must-be'));
          setTimeout(() => {
            this.$refs.passwordInput.focus();
          }, 200);
          break;
        default:
          break;
      }
    },
  },

  created() {
    this.inputs.email = this.email;
    this.$store.commit('authModal/setResetPasswordStep', ResetPasswordStep.Email);
  },

  methods: {
    complete(code) {
      this.inputsConfirm.code = code;
      this.$store.commit('authModal/setResetPasswordStep', ResetPasswordStep.Password);
    },
    async requestResetCode(resend = false) {
      this.$analytics.trackGA({ eventName: 'gx:identity:forgot-password:submit-attempt' });

      this.clearErrors();
      this.v$.inputs.$reset();
      this.$nextTick(() => {
        this.v$.inputsConfirm.$reset();
      });
      this.v$.inputs.$touch();
      if (this.v$.inputs.$invalid) {
        this.shakeErrors = true;
        this.removeShake();
        this.$analytics.trackGA({ eventName: 'gx:identity:forgot-password:submit-error' });

        return;
      }

      this.loading = true;

      this.$auth.requestResetCode(this.getPayload)
        .then(
          () => {
            this.$nextTick(() => {
              this.v$.inputsConfirm.$reset();
            });
            this.$store.commit('authModal/setResetPasswordStep', ResetPasswordStep.Code);
            this.startTimer();
            this.$analytics.trackGA({ eventName: 'gx:identity:forgot-password:submit-success' });
            this.$analytics.trackGA({ eventName: 'gx:identity:reset-password:load' });

            if (resend) {
              this.$toast(this.$t('login.verification-code-resent'));
            }
          },
          (error) => {
            this.$analytics.trackGA({ eventName: 'gx:identity:forgot-password:submit-fail' });
            this.parseError(error);
          },
        )
        .finally(() => {
          this.loading = false;
        });
    },
    async resetPassword() {
      this.$analytics.trackGA({ eventName: 'gx:identity:reset-password:submit-attempt' });
      this.clearErrors();
      this.v$.inputs.$reset();
      this.v$.inputsConfirm.$reset();
      this.v$.inputsConfirm.$touch();
      if (this.v$.inputsConfirm.$invalid) {
        this.shakeErrors = true;
        this.removeShake();
        this.$analytics.trackGA({ eventName: 'gx:identity:reset-password:submit-error' });

        return;
      }
      this.loading = true;
      this.$auth.resetPassword(this.getConfirmPayload)
        .then(
          () => {
            this.$analytics.trackGA({ eventName: 'gx:identity:reset-password:submit-success' });
            this.$emit('complete', this.getAuthPayload);
          },
          () => {
            this.inputsConfirm.code = '';
            this.inputsConfirm.password = '';
            this.$nextTick(() => {
              this.v$.inputsConfirm.$reset();
            });
            this.$toast(this.$t('login.verification-code-error-incorrect'), {
              status: 'error',
            });
            this.loading = false;
            this.$store.commit('authModal/setResetPasswordStep', ResetPasswordStep.Email);
          },
        );
    },
    startTimer() {
      this.timer = 60 * 1000; // 60 seconds
      const interval = setInterval(() => {
        this.timer -= 1000;
        if (this.timer <= 0) {
          clearInterval(interval);
        }
      }, 1000);
    },
  },

};
</script>
